import { ThemeMode, ColorTheme } from '../../../store/slices/themeSlice';

const THEME_STORAGE_KEY = 'guest-theme-preference';
const COLOR_THEME_STORAGE_KEY = 'guest-color-theme-preference';

// Single source of truth for the most recently applied theme — consumed by the
// blocking boot script in app/layout.tsx so the next page load paints in the
// right colors before React hydrates. Updated on every applyThemeToDocument()
// call so it always reflects whatever the user is actually seeing.
export const LAST_APPLIED_THEME_KEY = 'sm-last-applied-theme';

/**
 * Detect system theme preference
 */
export const detectSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') {
    return 'light'; // Server-side default
  }
  
  try {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    return mediaQuery.matches ? 'dark' : 'light';
  } catch (error) {
    console.warn('Failed to detect system theme:', error);
    return 'light';
  }
};

/**
 * Load guest theme preference from localStorage
 */
export const loadGuestThemePreference = (): { mode: ThemeMode; colorTheme: ColorTheme } | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
    const savedColorTheme = localStorage.getItem(COLOR_THEME_STORAGE_KEY) as ColorTheme;
    
    if (savedMode && savedColorTheme) {
      return { mode: savedMode, colorTheme: savedColorTheme };
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to load guest theme preference:', error);
    return null;
  }
};

/**
 * Save guest theme preference to localStorage
 */
export const saveGuestThemePreference = (mode: ThemeMode, colorTheme: ColorTheme): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, colorTheme);
  } catch (error) {
    console.warn('Failed to save guest theme preference:', error);
  }
};

/**
 * Clear guest theme preference from localStorage
 */
export const clearGuestThemePreference = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
    localStorage.removeItem(COLOR_THEME_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear guest theme preference:', error);
  }
};

/**
 * Apply theme to document
 */
export const applyThemeToDocument = (mode: ThemeMode, colorTheme: ColorTheme): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Apply theme mode
    let effectiveMode: 'light' | 'dark' = mode === 'system' ? detectSystemTheme() : mode;
    
    root.classList.add(effectiveMode);
    
    // Apply color theme by setting CSS variables
    applyColorTheme(colorTheme, effectiveMode);
    
    // Apply color theme data attribute
    root.setAttribute('data-color-theme', colorTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const isDark = effectiveMode === 'dark';
      metaThemeColor.setAttribute('content', isDark ? '#1f2937' : '#ffffff');
    }

    // Cache the applied theme so the boot script in layout.tsx can paint the
    // correct colors on the next page load without waiting for React hydration.
    try {
      localStorage.setItem(
        LAST_APPLIED_THEME_KEY,
        JSON.stringify({ mode: effectiveMode, colorTheme }),
      );
    } catch {
      /* localStorage unavailable — boot script will fall back to defaults */
    }

    console.log('Applied theme to document:', { mode: effectiveMode, colorTheme });
    
  } catch (error) {
    console.warn('Failed to apply theme to document:', error);
  }
};

/**
 * Apply color theme by setting CSS variables
 */
const applyColorTheme = (colorTheme: ColorTheme, effectiveMode: 'light' | 'dark'): void => {
  const root = document.documentElement;
  
  // Define color theme palettes (matching the previous color palette definitions)
  const colorThemes = {
    yellow: {
      light: {
        primary: '43 96% 40%',         // Deeper amber for better readability
        accent: '45 93% 35%',          // Slightly darker accent
        ring: '43 96% 40%',            // Same as primary
      },
      dark: {
        primary: '45 93% 47%',         // Bright yellow for dark backgrounds
        accent: '43 96% 40%',          // Deeper yellow accent
        ring: '45 93% 47%',            // Same as primary
      }
    },
    green: {
      light: {
        primary: '142 71% 38%',        // Deeper emerald for better readability
        accent: '142 76% 30%',         // Darker green accent
        ring: '142 71% 38%',           // Same as primary
      },
      dark: {
        primary: '142 71% 45%',        // Bright green for dark backgrounds
        accent: '142 71% 38%',         // Deeper green accent
        ring: '142 71% 45%',           // Same as primary
      }
    },
    blue: {
      light: {
        primary: '221 83% 45%',        // Deeper blue for better readability
        accent: '217 91% 50%',         // Vibrant blue accent
        ring: '221 83% 45%',           // Same as primary
      },
      dark: {
        primary: '217 91% 60%',        // Bright blue for dark backgrounds
        accent: '221 83% 53%',         // Medium blue accent
        ring: '217 91% 60%',           // Same as primary
      }
    },
    purple: {
      light: {
        primary: '262 83% 50%',        // Deeper purple for better readability
        accent: '263 70% 42%',         // Rich purple accent
        ring: '262 83% 50%',           // Same as primary
      },
      dark: {
        primary: '270 91% 65%',        // Bright purple for dark backgrounds
        accent: '262 83% 58%',         // Medium purple accent
        ring: '270 91% 65%',           // Same as primary
      }
    }
  };
  
  const colors = colorThemes[colorTheme][effectiveMode];
  
  // Apply CSS variables
  root.style.setProperty('--primary', colors.primary);
  root.style.setProperty('--accent', colors.accent);
  root.style.setProperty('--ring', colors.ring);
  
  // Also set the primary-foreground and accent-foreground for better contrast
  const foregroundColor = effectiveMode === 'dark' ? '0 0% 98%' : '0 0% 9%';
  root.style.setProperty('--primary-foreground', foregroundColor);
  root.style.setProperty('--accent-foreground', foregroundColor);
  
  console.log('Applied color theme:', { colorTheme, effectiveMode, colors });
};

/**
 * Update HTML lang and dir attributes for theme
 */
export const updateHtmlAttributes = (mode: ThemeMode, colorTheme: ColorTheme): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    const html = document.documentElement;
    
    // Set theme attributes
    html.setAttribute('data-theme', mode);
    html.setAttribute('data-color-theme', colorTheme);
    
    // Apply theme classes
    applyThemeToDocument(mode, colorTheme);
  } catch (error) {
    console.warn('Failed to update HTML attributes:', error);
  }
};